import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, Mail, Eye, EyeOff, ChevronRight } from "lucide-react";
import Logo from "@/components/layout/Logo";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import TermsModal from "@/components/ui/TermsModal";
import { DRIVER_TERMS_TEXT } from "@/data/legalContent";

export default function DriverLogin() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // The PWA home-screen shortcut opens here directly (manifest start_url) so a
  // driver with a still-valid session shouldn't have to re-enter credentials
  // every time they tap the icon — bounce straight to their runs instead.
  if (!loading && user && user.role === "driver") {
    return <Navigate to="/driver-dashboard/runs" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "driver") {
        toast.error("This portal is for drivers only. Use the Admin/Client login.");
        return;
      }
      toast.success(`Welcome back, ${u.name}!`);
      nav("/driver-dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="driver-bg min-h-screen text-white flex flex-col relative overflow-hidden">

      {/* ── Abstract background — amber glow orbs + diagonal motion lines,
          replacing the old truck photo with brand-toned abstract art ── */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" style={{ background: "#0a0a0f" }}>
        <div
          className="absolute -top-40 -left-24 w-[560px] h-[560px] rounded-full opacity-25"
          style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)", filter: "blur(70px)" }}
        />
        <div
          className="absolute -bottom-48 -right-28 w-[620px] h-[620px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #b45309, transparent 70%)", filter: "blur(70px)" }}
        />
        <div
          className="absolute top-1/4 right-[15%] w-[420px] h-[420px] rounded-full opacity-[0.15]"
          style={{ background: "radial-gradient(circle, #fb923c, transparent 70%)", filter: "blur(60px)" }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(115deg, #f59e0b 0px, #f59e0b 1px, transparent 1px, transparent 90px)" }}
        />
      </div>
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: "linear-gradient(rgba(10,10,15,0.72), rgba(10,10,15,0.85))" }}
      />

      {/* Header */}
      <header className="driver-header relative z-10 px-6 py-4 flex items-center justify-between">
        <Logo size={36} />
        <Link
          to="/login"
          className="flex items-center gap-1 text-sm text-white/50 hover:text-white/80 transition-colors"
        >
          Admin / Client Login <ChevronRight size={14} />
        </Link>
      </header>

      {/* Body */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-up">
          {/* Logo area */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-5">
              <Logo size={80} />
            </div>
            <h1 className="brand-title text-3xl" style={{ color: "#f59e0b" }}>
              DRIVER PORTAL
            </h1>
            <p className="text-white/50 mt-2 text-sm">
              Sign in to manage your runs &amp; stay connected
            </p>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Mail
                size={17}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400/60 pointer-events-none"
              />
              <input
                type="email"
                placeholder="Driver email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="driver-input"
                style={{ paddingLeft: "44px" }}
                data-testid="driver-login-email"
              />
            </div>

            <div className="relative">
              <Lock
                size={17}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400/60 pointer-events-none"
              />
              <input
                type={show ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="driver-input"
                style={{ paddingLeft: "44px", paddingRight: "44px" }}
                data-testid="driver-login-password"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white z-10"
                aria-label="toggle password"
              >
                {show ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="driver-submit-btn w-full mt-2"
              data-testid="driver-login-submit"
            >
              {busy ? "Signing in…" : "SIGN IN"}
            </button>

            <p className="text-center text-xs text-white/50 mt-4">
              By logging in, you agree to our{" "}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-red-500 hover:text-red-400 font-semibold"
                data-testid="driver-terms-link"
              >
                Terms and Conditions
              </button>.
            </p>
          </form>

          <p className="text-center text-white/30 text-xs mt-6 leading-relaxed">
            Only authorised drivers can access this portal.
            <br />
            Contact dispatch if you need access.
          </p>

        </div>
      </div>

      {showTerms && (
        <TermsModal
          title="Terms and Conditions for Drivers and Independent Sub-Contractors"
          text={DRIVER_TERMS_TEXT}
          onClose={() => setShowTerms(false)}
        />
      )}

    </div>
  );
}
